import {
  calculatePayment,
  getAmountToFinance,
  getIncomeAndDebtBreakdown,
  calculateLTV,
  getBig,
  calculateMaxAmountToFinanceForTier,
  getAmortizationSchedule,
  getSelectedFinanceQuote,
  debtToIncome,
  paymentToIncome
} from './quoteUtils';
import produce from 'immer';
import copy from "fast-copy";
import { BusinessAction } from '../Data/BusinessRulesData';
import { ApplicationBudget } from '../Data/ApplicationBudgetData';
import { ApplicationState } from '../store/application/types';
import {
  getAge, getCustomField
} from './appUtils';
import { IRuleViolation } from '../Interfaces/Application/IApplicationQuote';
import { DealerServiceState } from '../store/dealerService/reducer';
import { INotificationsState } from '../store/Notifications/reducer';
import {getFinanceQuoteAmountFinanced, getFinanceQuoteValues} from "./LoanCalcUtils";
import { getMonthsElapsed } from "@amsanalytics/react-shared-library/build/Helpers/Utilities";
import {getPaymentToIncomeRatioWithPayment, getThresholdSectionForValue, getTotalRepossessions} from './utils';
import {getAppQuoteByType} from "../Components/Quoters/IndirectQuoter/IndirectQuoterUtils";
import {
  getLeaseQuote,
  getLeaseFullPayment,
  getLeaseBasePayment,
  getLeaseTotalOfPayments,
  getLeaseTotalFees,
  getLeaseCashDown,
  getLeaseDeferredDownPayments,
  getLeaseTotalDeferredDown,
  getLeasePaymentToIncome,
  getLeaseDebtToIncome,
  getLeaseMaturityDate,
  getLeaseAmortizationSchedule,
} from './leaseRuleHelpers';
import {
  add,
  ApplicationQuoteType,
  divide,
  getNumberOfPaymentsPerYearForCarletonPaymentFrequency,
  multiply,
  pow,
  roundNumber,
  subtract
} from "@amsanalytics/react-shared-library";
import Big from 'big.js';

export const onAction = (action: Action, context: Context): ActionResult => {
  if (context.attachDebugger) {
    debugger;
  }

  const actionAndContext = {
    action,
    context,
  };

  const warnings: string[] = [];
  const errors: (string | IRuleViolation)[] = [];

  const nextState = produce(actionAndContext, actionAndContext => {
    actionAndContext = context.debugMode ? copy(actionAndContext) : actionAndContext;

    for (const rule of (context.rules || [])) {
      //const ruleAction = context.ruleLookups[rule.type];

      if (rule.code) {
        try {
          actionAndContext.context.ruleId = rule.id;
          const func = new Function("action", "context", rule.code);
          let result = func(actionAndContext.action, actionAndContext.context);
          // Handle the result
          if (result !== undefined) {
            if (rule.type === 'Config') {
              if (typeof result === 'object') {
                actionAndContext.context.config = {
                  ...actionAndContext.context.config,
                  ...result,
                };
              }
            } else if (rule.type === 'Validator') {
              if (typeof result === 'boolean' && !result) {
                errors.push(`Validation rule failed: ${rule.description}`);
              } else if (typeof result === 'string') {
                errors.push(result);
              } else if (Array.isArray(result)) {
                const ruleViolations = result as IRuleViolation[];
                for (const ruleViolation of ruleViolations) {
                  errors.push(ruleViolation);
                }
              }
            } else if (rule.type === 'Event') {
              warnings.push(result.toString());
            }
          }
        } catch (e) {
          if (typeof e === 'string') {
            errors.push(`Rule: ${rule.description}- ${e}`);
          } else if (e instanceof Error) {
            errors.push(`Rule: ${rule.description}- ${e.stack || e.message}`);
          } else {
            errors.push(`Rule: ${rule.description}- ${JSON.stringify(e)}`);
          }
        }
      }
    }

    if (context.debugMode) {
      return actionAndContext;
    }
  });

  const result: ActionResult = {
    applicationState: nextState.context.applicationState,
    dealerServiceState: nextState.context.dealerServiceState,
    notificationsState: nextState.context.notificationsState,
    warnings,
    errors,
  };

  return result;
}

export interface Action {
  action: BusinessAction,
  attemptedChange?: JsonPatch[],
  applicationBudget?: ApplicationBudget,
}

export type JsonPatch<V = any> = {
  op: 'add' | 'replace' | 'test',
  path: string;
  value: V;
} | {
  op: 'move' | 'copy',
  path: string;
  from: string;
} | {
  op: 'remove',
  path: string;
};


export interface Context {
  applicationState?: ApplicationState;
  dealerServiceState?: DealerServiceState;
  notificationsState?: INotificationsState;
  currentDate?: Date;
  rules?: BusinessRule[];
  debugMode?: boolean;
  excludePaymentFromDebtToIncome?: boolean;
  attachDebugger?: boolean;
  config?: Record<string, any>;
  ruleId?: number; // ID of the business rule being processed
  utilities: {
    getAge: typeof getAge,
    multiply: typeof multiply,
    divide: typeof divide,
    pow: typeof pow,
    add: typeof add,
    subtract: typeof subtract,
    getAmountToFinance: typeof getAmountToFinance,
    getIncomeAndDebtBreakdown: typeof getIncomeAndDebtBreakdown,
    calculatePayment: typeof calculatePayment,
    calculateLTV: typeof calculateLTV,
    getBig: typeof getBig,
    getCustomField: typeof getCustomField,
    roundNumber: typeof roundNumber,
    calculateMaxAmountToFinanceForTier: typeof calculateMaxAmountToFinanceForTier,
    getAmortizationSchedule: typeof getAmortizationSchedule,
    getFinanceQuoteValues: typeof getFinanceQuoteValues,
    constants: any,
    getSelectedFinanceQuote: typeof getSelectedFinanceQuote,
    getMonthsElapsed: typeof getMonthsElapsed,
    debtToIncome: typeof debtToIncome,
    paymentToIncome: typeof paymentToIncome,
    getNumberOfPaymentsPerYearForCarletonPaymentFrequency: typeof getNumberOfPaymentsPerYearForCarletonPaymentFrequency,
    applicationQuoteTypes: typeof ApplicationQuoteType,
    big: typeof Big,
    getThresholdSectionForValue: typeof getThresholdSectionForValue,
    getAppQuoteByType: typeof getAppQuoteByType,
    getFinanceQuoteAmountFinanced: typeof getFinanceQuoteAmountFinanced,
    getTotalRepossessions: typeof getTotalRepossessions,
    getPaymentToIncomeRatioWithPayment: typeof getPaymentToIncomeRatioWithPayment,
    getLeaseQuote: typeof getLeaseQuote,
    getLeaseFullPayment: typeof getLeaseFullPayment,
    getLeaseBasePayment: typeof getLeaseBasePayment,
    getLeaseTotalOfPayments: typeof getLeaseTotalOfPayments,
    getLeaseTotalFees: typeof getLeaseTotalFees,
    getLeaseCashDown: typeof getLeaseCashDown,
    getLeaseDeferredDownPayments: typeof getLeaseDeferredDownPayments,
    getLeaseTotalDeferredDown: typeof getLeaseTotalDeferredDown,
    getLeasePaymentToIncome: typeof getLeasePaymentToIncome,
    getLeaseDebtToIncome: typeof getLeaseDebtToIncome,
    getLeaseMaturityDate: typeof getLeaseMaturityDate,
    getLeaseAmortizationSchedule: typeof getLeaseAmortizationSchedule,
  }
}

export interface BusinessRule {
  id: number;
  description?: string;
  type?: string;
  code?: string;
  deleted?: boolean;
  createdDate?: string;
  modifiedDate?: string;
  businessRuleSetId?: number;
  businessRuleActions?: BusinessRuleAction[];
}

export interface BusinessRuleAction {
  id: number;
  businessRuleId: number;
  action: BusinessAction;
  sortOrder?: number;
  deleted?: boolean;
  createdDate?: string;
  modifiedDate?: string;
}

export interface BusinessRuleOverride {
  id: string;
  applicationId: string;
  businessRuleId: number;
  businessRuleOverrideStatusId: string | null;
  actualValue?: string; // Actual value that was calculated
  thresholdValue?: string // Threshold value for determining pass/fail
  applicationField: string; // Field in the application that the rule applies to (for displaying error messages)
  validationFieldName?: string;
  applicationFieldDisplayName?: string; // Display name for the field for the grid UI
  reason?: string; // Reason for the override
  modifiedDate: Date;
  modifiedBy: string;
  sortOrder: number;
}

export interface BusinessRuleOverrideStatus {
  id: string;
  name: string;
  description?: string;
}

export interface ActionResult {
  applicationState?: ApplicationState,
  dealerServiceState?: DealerServiceState,
  notificationsState?: INotificationsState,
  warnings?: string[],
  errors?: (string | IRuleViolation)[],
}

export interface BusinessRuleSet {
  id: number;
  description?: string;
  startDate?: string;
  deleted?: boolean;
  createdDate?: string;
  modifiedDate?: string;
}
